import { useCallback } from 'react';
import { useRecoilCallback } from 'recoil';
import { logger } from 'modules/logger';
import { reporterSavingState, ReporterSaveState } from 'config/recoilState';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { ReportStatus } from '../state';
import type { ReportStatuses } from '../state';
import type { SlateContent } from '../types';
import type { Addendum } from './fieldsReducer';
import { useAutoSaveReport, type UpdateType } from './useAutoSaveReport';

type UseAutoSaveReportReturn = ReturnType<typeof useAutoSaveReport>;

/**
 * Owns the explicit draft-save workflow. Decides report-draft vs addendum-draft based on status and
 * delegates the actual persistence to useAutoSaveReport (fieldsUpdate / addendumUpdate). Records the
 * just-drafted smid so autoload excludes it for the tab session (WF-1809).
 */
export interface UseDraftReportParams {
  setSavingState: UseAutoSaveReportReturn['setSavingState'];
  fieldsUpdate: UseAutoSaveReportReturn['fieldsUpdate'];
  addendumUpdate: UseAutoSaveReportReturn['addendumUpdate'];
  autoloadTemplateState: AutoloadTemplateState | undefined;
  worklistItem: LoadedReportWorklistItem;
  status: ReportStatuses;
  getCurrentReportContent: () => SlateContent;
  getCurrentReportUpdateType: () => Nullable<UpdateType>;
  activateTemplateID: string | undefined;
  addendum: Nullable<Addendum>;
  addendumSMID: Nullable<string>;
  isRecording: boolean;
  stopRecording: () => Promise<void>;
  loadDiscardedSmidsFromSessionStorage: () => Set<string>;
  saveDiscardedSmidsInSessionStorage: (discardedSmids: Set<string>) => void;
}

export const useDraftReport = ({
  setSavingState,
  fieldsUpdate,
  addendumUpdate,
  autoloadTemplateState,
  worklistItem,
  status,
  getCurrentReportContent,
  getCurrentReportUpdateType,
  activateTemplateID,
  addendum,
  addendumSMID,
  isRecording,
  stopRecording,
  loadDiscardedSmidsFromSessionStorage,
  saveDiscardedSmidsInSessionStorage,
}: UseDraftReportParams) => {
  const getSavingState = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(reporterSavingState).getValue(),
    []
  );

  const handleSaveDraft = useCallback(
    async ({
      onSuccess,
      onError,
    }: {
      onSuccess?: () => void;
      onError?: () => void;
    } = {}) => {
      if (getSavingState() === ReporterSaveState.Saving) {
        return;
      }

      if (autoloadTemplateState === 'pending') {
        logger.warn(
          '[useDraftReport] Attempted to save draft while template autoload is still pending',
          {
            worklistItemSmid: worklistItem.smid,
            status,
          }
        );
        onError?.();
        return;
      }

      // WF-1809: Records the just-drafted SMID so autoload excludes it for the
      // rest of the tab session - same bucket as discard. Without this, drafted
      // cases stayed eligible and the FE's items.find() looped between the two
      // earliest-sorted cases.
      const recordDiscardedSmid = () => {
        const discardedSmids = loadDiscardedSmidsFromSessionStorage();
        discardedSmids.add(worklistItem.smid);
        saveDiscardedSmidsInSessionStorage(discardedSmids);
      };

      if (status !== ReportStatus.AddendumAdd) {
        const content = getCurrentReportContent();
        setSavingState(ReporterSaveState.Saving);
        await fieldsUpdate({
          contentToSubmit: content,
          updateType: getCurrentReportUpdateType(),
          activateTemplateIDToSubmit: activateTemplateID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else if (status === ReportStatus.AddendumAdd && addendumSMID != null) {
        setSavingState(ReporterSaveState.Saving);
        await addendumUpdate({
          contentToSubmit: addendum?.content,
          addendumSMID,
          isExplicitDraftSave: true,
          onSuccess: () => {
            recordDiscardedSmid();
            onSuccess?.();
          },
          onError,
        });
      } else {
        const content = getCurrentReportContent();
        logger.info('[useDraftReport] Draft report did not save.', {
          status,
          addendumSMID,
          worklistItemSmid: worklistItem.smid,
          content: status === ReportStatus.AddendumAdd ? addendum?.content : content,
        });
      }

      if (isRecording) {
        await stopRecording();
      }
    },
    [
      autoloadTemplateState,
      getSavingState,
      status,
      addendumSMID,
      isRecording,
      setSavingState,
      fieldsUpdate,
      getCurrentReportContent,
      getCurrentReportUpdateType,
      activateTemplateID,
      addendumUpdate,
      addendum?.content,
      worklistItem.smid,
      stopRecording,
      loadDiscardedSmidsFromSessionStorage,
      saveDiscardedSmidsInSessionStorage,
    ]
  );

  return { handleSaveDraft };
};
